import React from 'react';
import { Button, Chip, IconButton, Menu, MenuItem } from '@mui/material';
import { ArrowDownward, ArrowUpward, Circle, Reply, Warning } from '@mui/icons-material';
import {
	BroadcastReplyMode,
	canReplyToAll,
	canReplyToAuthor,
	isReplyToCurrentUser,
	ReplyPermissionFlags,
} from '../utils/broadcastReply';

export type PriorityMeta = {
	name: string;
	color: string;
	background: string;
	order: number;
	Icon: typeof Warning;
};

export function getPriorityMeta(priorityLabel?: string | null): PriorityMeta {
	const label = (priorityLabel || '').toLowerCase();
	if (label === 'critical' || label === 'urgent') {
		return { name: 'Urgent', color: '#7f1d1d', background: '#fee2e2', order: 1, Icon: Warning };
	}
	if (label === 'high') {
		return { name: 'High', color: '#9a3412', background: '#ffedd5', order: 2, Icon: ArrowUpward };
	}
	if (label === 'medium') {
		return { name: 'Medium', color: '#14532d', background: '#dcfce7', order: 3, Icon: Circle };
	}
	if (label === 'low') {
		return { name: 'Low', color: '#1e3a8a', background: '#dbeafe', order: 4, Icon: ArrowDownward };
	}
	return { name: priorityLabel || 'N/A', color: '#374151', background: '#f3f4f6', order: 5, Icon: Circle };
}

export const BroadcastPriorityPill = ({ label }: { label?: string | null }) => {
	const meta = getPriorityMeta(label);
	const Icon = meta.Icon;
	return (
		<Chip
			size="small"
			icon={<Icon aria-hidden sx={{ color: `${meta.color} !important`, fontSize: '16px !important' }} />}
			label={meta.name}
			aria-label={`Priority: ${meta.name}`}
			sx={{
				height: 26,
				maxWidth: '100%',
				fontWeight: 700,
				fontSize: '12px',
				color: meta.color,
				backgroundColor: meta.background,
				border: `1px solid ${meta.color}`,
				'& .MuiChip-label': { px: 0.75 },
			}}
		/>
	);
};

type BroadcastReplyActionsProps = {
	message: unknown;
	permissions: ReplyPermissionFlags;
	onReply: (mode: BroadcastReplyMode) => void;
	layout?: 'menu' | 'buttons';
	size?: 'small' | 'medium';
	/** Original broadcast flag. Thread rows do not carry isAllowReplyAll themselves. */
	allowReplyAll?: boolean;
	/** Message whose sender is the reply-to-author target. Defaults to message. */
	senderMessage?: unknown;
};

const BroadcastReplyActions: React.FC<BroadcastReplyActionsProps> = ({
	message,
	permissions,
	onReply,
	layout = 'buttons',
	size = 'medium',
	allowReplyAll,
	senderMessage,
}) => {
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const currentUserId = Number(localStorage.getItem('userId') || '0') || null;
	const authorSource = senderMessage ?? message;
	const showAuthor = canReplyToAuthor(permissions, authorSource, currentUserId);
	const showAll =
		canReplyToAll(message, permissions, allowReplyAll, currentUserId) &&
		!isReplyToCurrentUser(authorSource, 'all', currentUserId);

	if (!showAuthor && !showAll) return null;

	if (layout === 'menu') {
		return (
			<>
				<IconButton
					aria-label="Reply"
					sx={{ color: 'rgb(68, 99, 136)' }}
					onClick={(event) => {
						event.stopPropagation();
						setAnchorEl(event.currentTarget);
					}}
				>
					<Reply style={{ fontSize: '20px' }} />
				</IconButton>
				<Menu
					anchorEl={anchorEl}
					open={Boolean(anchorEl)}
					onClose={() => setAnchorEl(null)}
					onClick={(event) => event.stopPropagation()}
				>
					{showAuthor && (
						<MenuItem
							onClick={(event) => {
								event.stopPropagation();
								setAnchorEl(null);
								onReply('author');
							}}
						>
							Reply to author
						</MenuItem>
					)}
					{showAll && (
						<MenuItem
							onClick={(event) => {
								event.stopPropagation();
								setAnchorEl(null);
								onReply('all');
							}}
						>
							Reply to all
						</MenuItem>
					)}
				</Menu>
			</>
		);
	}

	return (
		<>
			{showAuthor && (
				<Button
					size={size}
					variant="outlined"
					startIcon={<Reply />}
					onClick={() => onReply('author')}
					sx={{ borderColor: 'rgb(68, 99, 136)', color: 'rgb(68, 99, 136)', whiteSpace: 'nowrap' }}
				>
					Reply to author
				</Button>
			)}
			{showAll && (
				<Button
					size={size}
					variant="contained"
					startIcon={<Reply />}
					onClick={() => onReply('all')}
					sx={{ backgroundColor: 'rgb(68, 99, 136)', whiteSpace: 'nowrap' }}
				>
					Reply to all
				</Button>
			)}
		</>
	);
};

export default BroadcastReplyActions;
